export type ParsedSlashDueDate = {
  year: number;
  month: number;
  day: number;
  label: string;
  iso: string;
};

const MONTH_INDEX: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12
};

const DUE_PREFIX = /^(?:due(?:\s*date)?|deadline|on)\s+/i;

export function localePrefersMonthFirst(locale: string): boolean {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    }).formatToParts(new Date(2026, 9, 25));
    const monthIndex = parts.findIndex(part => part.type === 'month');
    const dayIndex = parts.findIndex(part => part.type === 'day');
    return monthIndex >= 0 && dayIndex >= 0 && monthIndex < dayIndex;
  } catch {
    return false;
  }
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

export function formatDueDateLabel(year: number, month: number, day: number): string {
  return `${day} ${MONTH_LABELS[month - 1]} ${year}`;
}

export function dueAtIsoFromParts(year: number, month: number, day: number): string {
  return new Date(year, month - 1, day).toISOString();
}

function isValidYmd(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
    return false;
  }
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function expandYear(year: number): number {
  if (year < 100) return 2000 + year;
  return year;
}

function result(year: number, month: number, day: number): ParsedSlashDueDate | null {
  if (!isValidYmd(year, month, day)) return null;
  return {
    year,
    month,
    day,
    label: formatDueDateLabel(year, month, day),
    iso: dueAtIsoFromParts(year, month, day)
  };
}

function resolveDayMonth(
  first: number,
  second: number,
  year: number,
  monthFirst: boolean
): ParsedSlashDueDate | null {
  if (first > 12 && second <= 12) return result(year, second, first);
  if (second > 12 && first <= 12) return result(year, first, second);
  if (first > 12 && second > 12) return null;
  if (monthFirst) return result(year, first, second);
  return result(year, second, first);
}

function localTodayParts(now: Date): { year: number; month: number; day: number } {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

function addDays(now: Date, days: number): ParsedSlashDueDate | null {
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  return result(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

function parseRelative(text: string, now: Date): ParsedSlashDueDate | null {
  if (text === 'today') return addDays(now, 0);
  if (text === 'tomorrow') return addDays(now, 1);
  if (text === 'yesterday') return addDays(now, -1);
  if (text === 'next week') return addDays(now, 7);
  return null;
}

function parseNamedMonth(text: string, now: Date): ParsedSlashDueDate | null {
  const dayFirst = text.match(
    /^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+)\.?(?:\s+(\d{2,4}))?$/i
  );
  if (dayFirst) {
    const month = MONTH_INDEX[dayFirst[2].toLowerCase()];
    if (!month) return null;
    const year = dayFirst[3] ? expandYear(Number(dayFirst[3])) : now.getFullYear();
    return result(year, month, Number(dayFirst[1]));
  }

  const monthFirst = text.match(
    /^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:\s+(\d{2,4}))?$/i
  );
  if (monthFirst) {
    const month = MONTH_INDEX[monthFirst[1].toLowerCase()];
    if (!month) return null;
    const year = monthFirst[3] ? expandYear(Number(monthFirst[3])) : now.getFullYear();
    return result(year, month, Number(monthFirst[2]));
  }

  return null;
}

function parseNumeric(text: string, monthFirst: boolean, now: Date): ParsedSlashDueDate | null {
  const iso = text.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);
  if (iso) {
    return result(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  }

  const three = text.match(/^(\d{1,4})[./\-\s]+(\d{1,2})[./\-\s]+(\d{1,4})$/);
  if (three) {
    const a = Number(three[1]);
    const b = Number(three[2]);
    const c = Number(three[3]);
    if (three[1].length === 4) {
      return result(a, b, c);
    }
    if (three[3].length === 4 || c > 31) {
      return resolveDayMonth(a, b, expandYear(c), monthFirst);
    }
    if (a > 31) {
      return result(expandYear(a), b, c);
    }
    return resolveDayMonth(a, b, expandYear(c), monthFirst);
  }

  const two = text.match(/^(\d{1,2})[./\-\s]+(\d{1,2})$/);
  if (two) {
    const today = localTodayParts(now);
    return resolveDayMonth(Number(two[1]), Number(two[2]), today.year, monthFirst);
  }

  return null;
}

export function parseSlashDueDate(
  query: string,
  options?: { locale?: string; now?: Date }
): ParsedSlashDueDate | null {
  const locale =
    options?.locale ||
    (typeof navigator !== 'undefined' ? navigator.language : 'en-AU');
  const now = options?.now || new Date();
  const trimmed = query.trim().replace(/,/g, ' ').replace(/\s+/g, ' ');
  if (!trimmed) return null;

  const withoutPrefix = trimmed.replace(DUE_PREFIX, '').replace(/(\d)(?:st|nd|rd|th)\b/gi, '$1').trim();
  if (!withoutPrefix) return null;

  const text = withoutPrefix.toLowerCase();
  return (
    parseRelative(text, now) ||
    parseNamedMonth(text, now) ||
    parseNumeric(text, localePrefersMonthFirst(locale), now)
  );
}
